import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { DateRangePicker } from '../components/ui/date-range-picker';

function fmtBadgeDate(iso) {
    if (!iso) return '';
    const [y, m, day] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[+m - 1]} ${day}, ${y}`;
}

export default function Contact() {
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [dateRange, setDateRange] = useState({ from_date: '', to_date: '' });

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');

        api.getContacts({
            limit: pageSize,
            offset: (page - 1) * pageSize,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(dateRange.from_date ? { from_date: dateRange.from_date } : {}),
            ...(dateRange.to_date ? { to_date: dateRange.to_date } : {}),
        }).then((data) => {
            if (cancelled) return;
            setRecords(data.data || []);
            setTotal(data.total || 0);
        }).catch((err) => {
            if (cancelled) return;
            setError(err.message || 'Failed to load records');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => { cancelled = true; };
    }, [page, pageSize, debouncedSearch, dateRange.from_date, dateRange.to_date]);

    const handleDownload = async (id, docName) => {
        try {
            const blob = await api.getContactDocument(id);
            if (!blob) return;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = docName || 'document';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch {
            alert('Failed to download file');
        }
    };

    const formatDate = (dt) =>
        new Date(dt).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });

    const hasActiveFilter = debouncedSearch || dateRange.from_date || dateRange.to_date;

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            <div style={styles.topBar}>
                <div>
                    <h2 style={styles.title}>All Records</h2>
                    <p style={styles.subtitle}>
                        <span style={styles.countBadge}>{total}</span>
                        &nbsp;total record{total !== 1 ? 's' : ''} in the system
                    </p>
                </div>
                <div style={styles.topActions}>
                    <div style={styles.searchWrap}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Search by name, email or mobile..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>
            </div>

            {/* Date range filter */}
            <div style={styles.filterRow}>
                <span style={styles.filterLabel}>Filter by Date:</span>
                <DateRangePicker
                    value={dateRange}
                    showValue={!!(dateRange.from_date || dateRange.to_date)}
                    placeholder="Select date range"
                    onChange={(range) => {
                        setDateRange({
                            from_date: range.from_date || '',
                            to_date: range.to_date || '',
                        });
                        setPage(1);
                    }}
                    onClear={() => {
                        setDateRange({ from_date: '', to_date: '' });
                        setPage(1);
                    }}
                />
                {(dateRange.from_date || dateRange.to_date) && (
                    <span style={styles.activeFilterBadge}>
                        {dateRange.from_date && dateRange.to_date
                            ? `${fmtBadgeDate(dateRange.from_date)} → ${fmtBadgeDate(dateRange.to_date)}`
                            : dateRange.from_date
                            ? `From ${fmtBadgeDate(dateRange.from_date)}`
                            : `Until ${fmtBadgeDate(dateRange.to_date)}`}
                    </span>
                )}
            </div>

            {error && (
                <div style={styles.errorBox}>
                    <span>⚠</span> {error}
                </div>
            )}

            {loading ? (
                <div style={styles.loaderWrap}>
                    <div style={styles.spinnerRing}>
                        <div style={styles.spinner} />
                    </div>
                    <p style={{ color: '#78716c', marginTop: 16, fontWeight: 500 }}>Loading records...</p>
                </div>
            ) : records.length === 0 ? (
                <div style={styles.emptyWrap}>
                    <div style={styles.emptyIconWrap}>
                        <span style={{ fontSize: 36 }}>📭</span>
                    </div>
                    <p style={styles.emptyTitle}>
                        {hasActiveFilter ? 'No matching records found' : 'No records yet'}
                    </p>
                    <p style={styles.emptyText}>
                        {hasActiveFilter
                            ? 'Try adjusting your search or date filter.'
                            : 'No contact records available.'}
                    </p>
                </div>
            ) : (
                <>
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.thead}>
                                    <th style={styles.th}>SNO</th>
                                    <th style={styles.th}>Name</th>
                                    <th style={styles.th}>Mobile</th>
                                    <th style={styles.th}>Email</th>
                                    <th style={styles.th}>Remarks</th>
                                    <th style={styles.th}>Created At</th>
                                    <th style={styles.th}>Document</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((r, i) => (
                                    <tr
                                        key={r.id}
                                        style={{ ...styles.tr, ...(i % 2 === 0 ? styles.trEven : {}) }}
                                    >
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {(page - 1) * pageSize + i + 1}
                                        </td>
                                        <td style={{ ...styles.td, fontWeight: 700, color: '#1c1917' }}>
                                            <div style={styles.nameCell}>{r.name}</div>
                                        </td>
                                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 13 }}>{r.mobile}</td>
                                        <td style={styles.td}>
                                            <a href={`mailto:${r.email}`} style={styles.emailLink}>{r.email}</a>
                                        </td>
                                        <td style={{ ...styles.td, maxWidth: 160 }}>
                                            <span style={styles.remarks}>
                                                {r.remarks || <span style={{ color: '#d6d3d1' }}>—</span>}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: '#78716c' }}>
                                            {formatDate(r.created_at)}
                                        </td>
                                        <td style={styles.td}>
                                            {r.document_name ? (
                                                <button
                                                    onClick={() => handleDownload(r.id, r.document_name)}
                                                    style={styles.downloadBtn}
                                                    title={r.document_name}
                                                >
                                                    ⬇ {r.document_name.length > 16 ? r.document_name.slice(0, 14) + '…' : r.document_name}
                                                </button>
                                            ) : (
                                                <span style={styles.noDoc}>No file</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={styles.pagination}>
                        <button
                            style={{ ...styles.pageBtn, ...(page <= 1 ? styles.pageBtnDisabled : {}) }}
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            ← Prev
                        </button>
                        <span style={styles.pageInfo}>
                            Page {page} of {totalPages}&nbsp;·&nbsp;{total} record{total !== 1 ? 's' : ''}
                        </span>
                        <button
                            style={{ ...styles.pageBtn, ...(page >= totalPages ? styles.pageBtnDisabled : {}) }}
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

const styles = {
    container: { padding: '0', maxWidth: 1280, margin: '0 auto' },
    topBar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 16,
    },
    title: { margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: '#1c1917', letterSpacing: -0.5 },
    subtitle: { margin: 0, color: '#78716c', fontSize: 14, display: 'flex', alignItems: 'center', gap: 4 },
    countBadge: {
        display: 'inline-block',
        background: '#ffedd5',
        color: '#c2410c',
        fontWeight: 700,
        fontSize: 13,
        padding: '1px 8px',
        borderRadius: 20,
        border: '1px solid #fed7aa',
    },
    topActions: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
    searchWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    searchIcon: {
        position: 'absolute',
        left: 12,
        fontSize: 14,
        pointerEvents: 'none',
        zIndex: 1,
    },
    searchInput: {
        border: '1.5px solid #e7e5e4',
        borderRadius: 12,
        padding: '9px 14px 9px 36px',
        fontSize: 14,
        outline: 'none',
        width: 300,
        color: '#1c1917',
        background: '#ffffff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    },
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 20,
        padding: '12px 16px',
        background: '#ffffff',
        borderRadius: 12,
        border: '1.5px solid #e7e5e4',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    },
    filterLabel: {
        fontSize: 13,
        fontWeight: 700,
        color: '#78716c',
        whiteSpace: 'nowrap',
    },
    activeFilterBadge: {
        background: '#ffedd5',
        color: '#c2410c',
        border: '1px solid #fed7aa',
        borderRadius: 20,
        padding: '3px 10px',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    errorBox: {
        background: '#fef2f2',
        border: '1px solid #fecaca',
        color: '#dc2626',
        padding: '11px 14px',
        borderRadius: 12,
        fontSize: 13,
        marginBottom: 16,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    loaderWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '72px 0',
    },
    spinnerRing: {
        width: 52,
        height: 52,
        borderRadius: '50%',
        background: '#ffedd5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    spinner: {
        width: 32,
        height: 32,
        border: '3px solid #fed7aa',
        borderTop: '3px solid #f97316',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    emptyWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '72px 0',
        gap: 8,
    },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: '#ffedd5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
    },
    emptyTitle: { margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#1c1917' },
    emptyText: { margin: '0 0 20px', color: '#78716c', fontSize: 14, textAlign: 'center', maxWidth: 300 },
    tableWrap: {
        background: '#ffffff',
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(194, 65, 12, 0.1)',
        overflow: 'auto',
        border: '1px solid #f3ede8',
    },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
    thead: { background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' },
    th: {
        padding: '14px 16px',
        textAlign: 'left',
        fontWeight: 700,
        fontSize: 11,
        color: 'rgba(255,255,255,0.9)',
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        whiteSpace: 'nowrap',
    },
    tr: { transition: 'background 0.15s', borderBottom: '1px solid #fef3ec' },
    trEven: { background: '#fffbf7' },
    td: { padding: '13px 16px', fontSize: 14, color: '#44403c', verticalAlign: 'middle' },
    tdNum: { color: '#a8a29e', fontSize: 13, fontWeight: 600, width: 40 },
    nameCell: { display: 'flex', alignItems: 'center', gap: 10 },
    emailLink: { color: '#ea580c', textDecoration: 'none', fontWeight: 500 },
    remarks: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        lineHeight: 1.4,
        fontSize: 13,
        color: '#78716c',
    },
    downloadBtn: {
        background: '#fff7ed',
        border: '1px solid #fed7aa',
        color: '#c2410c',
        padding: '5px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        transition: 'background 0.15s',
    },
    noDoc: { color: '#d6d3d1', fontSize: 13 },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        marginTop: 20,
        padding: '12px 0',
    },
    pageBtn: {
        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
        color: '#fff',
        border: 'none',
        padding: '8px 18px',
        borderRadius: 10,
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 13,
        boxShadow: '0 2px 8px rgba(249,115,22,0.3)',
        transition: 'opacity 0.15s',
    },
    pageBtnDisabled: {
        background: '#e7e5e4',
        color: '#a8a29e',
        boxShadow: 'none',
        cursor: 'not-allowed',
    },
    pageInfo: {
        fontSize: 14,
        color: '#78716c',
        fontWeight: 500,
        minWidth: 180,
        textAlign: 'center',
    },
};
